import type { AnimatedGifPlacement } from '../../types/animatedGif';

const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const sanitizeCoordinate = (value: number, fallback: number): number =>
  Math.max(0, Math.min(999, Math.floor(toNumber(value, fallback))));

const sanitizeOpacity = (value: number): number => Math.max(0.05, Math.min(1, toNumber(value, 1)));

const sanitizeScalePercent = (value: number): number =>
  Math.max(5, Math.min(2000, toNumber(value, 100)));

export const toAnimatedGifPlacement = (
  value: unknown,
  index: number,
): AnimatedGifPlacement | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const entry = value as Record<string, unknown>;
  const imagePath = typeof entry.imagePath === 'string' ? entry.imagePath.trim() : '';
  if (!imagePath) {
    return null;
  }

  return {
    id: String(entry.id ?? `animated-gif-${index + 1}`).trim() || `animated-gif-${index + 1}`,
    name: String(entry.name ?? `Animated GIF ${index + 1}`).trim() || `Animated GIF ${index + 1}`,
    imagePath,
    imageWidth: Math.max(1, Math.floor(toNumber(entry.imageWidth, 1))),
    imageHeight: Math.max(1, Math.floor(toNumber(entry.imageHeight, 1))),
    offsetX: sanitizeCoordinate(toNumber(entry.offsetX, 0), 0),
    offsetY: sanitizeCoordinate(toNumber(entry.offsetY, 0), 0),
    scalePercent: sanitizeScalePercent(toNumber(entry.scalePercent, 100)),
    avoidCropping: entry.avoidCropping !== false,
    opacity: sanitizeOpacity(toNumber(entry.opacity, 1)),
    visible: entry.visible !== false,
  };
};

export const sanitizeAnimatedGifPlacements = (
  value: AnimatedGifPlacement[] | undefined,
): AnimatedGifPlacement[] =>
  Array.isArray(value)
    ? value
        .map((entry, index) => toAnimatedGifPlacement(entry, index))
        .filter((entry): entry is AnimatedGifPlacement => entry !== null)
        .slice(0, 20)
    : [];
